// src/services/contact/contactService.js

import { auth, db } from '../../firebase';
import { doc, getDoc, getDocs, query, where, collection, limit, updateDoc } from 'firebase/firestore';
import { userSearchService } from './userSearchService';

class ContactService {
  // ── Singleton ──────────────────────────────────────────────────────────────

  constructor() {
    this._auth = auth;
    this._db = db;
    this._userSearchService = userSearchService;
  }

  static getInstance() {
    if (!ContactService._instance) {
      ContactService._instance = new ContactService();
    }
    return ContactService._instance;
  }

  // ── Permission ──────────────────────────────────────────────────────────────

  async requestContactsPermission() {
    try {
      // Check if the browser supports the Contacts API
      if (!navigator.contacts) {
        console.warn('Contacts API not supported in this browser');
        return false;
      }

      const contacts = await navigator.contacts.select(['name', 'tel'], { multiple: true });
      return contacts && contacts.length > 0;
    } catch (error) {
      console.error('Contacts permission error:', error);
      return false;
    }
  }

  // ── Phone normalizer ────────────────────────────────────────────────────────

  _normalizePhone(phone) {
    if (!phone) return '';
    
    let cleaned = phone.replace(/[^\d+]/g, '');

    if (cleaned.startsWith('0')) {
      cleaned = `+256${cleaned.substring(1)}`;
    }

    if (cleaned.startsWith('256') && !cleaned.startsWith('+')) {
      cleaned = `+${cleaned}`;
    }

    if (!cleaned.startsWith('+')) {
      cleaned = `+${cleaned}`;
    }

    return cleaned;
  }

  // ── Raw device contacts ─────────────────────────────────────────────────────

  async getDeviceContacts() {
    try {
      // Check if Contacts API is available
      if (!navigator.contacts) {
        console.warn('Contacts API not supported');
        return [];
      }

      const deviceContacts = await navigator.contacts.select(
        ['name', 'tel'],
        { multiple: true }
      );

      const contacts = [];

      for (const c of deviceContacts) {
        const phones = [];
        const name = c.name?.[0] || 'Unknown';

        if (c.tel) {
          for (const tel of c.tel) {
            const normalized = this._normalizePhone(tel);
            if (normalized.length >= 10) {
              phones.push(normalized);
            }
          }
        }

        if (phones.length > 0) {
          contacts.push({
            displayName: name,
            phones: phones,
            avatarUrl: null,
          });
        }
      }

      console.log(`Loaded ${contacts.length} device contacts`);
      return contacts;
    } catch (error) {
      console.error('Error getting device contacts:', error);
      return [];
    }
  }

  // ── Get Filatom contacts via batch query ──────────────────────────────────

  async getFilatomContactsOptimized() {
    const deviceContacts = await this.getDeviceContacts();

    const allPhones = [];
    const phoneSet = new Set();

    for (const c of deviceContacts) {
      for (const phone of c.phones) {
        if (phone.length >= 10 && !phoneSet.has(phone)) {
          phoneSet.add(phone);
          allPhones.push(phone);
        }
      }
    }

    if (allPhones.length === 0) return [];

    const user = this._auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    const phoneUserMap = {};

    try {
      const batchSize = 200;

      for (let i = 0; i < allPhones.length; i += batchSize) {
        const batch = allPhones.slice(i, i + batchSize);

        console.log(`Sending ${batch.length} phones to search`);

        // Query Firestore for users with these phone numbers
        const results = await this._userSearchService.searchUsersByPhones(batch);

        Object.assign(phoneUserMap, results);
      }
    } catch (error) {
      console.error('Filatom user search failed:', error);
    }

    const filatomContacts = [];

    for (const deviceContact of deviceContacts) {
      let found = false;

      for (const phone of deviceContact.phones) {
        const normalizedPhone = this._normalizePhone(phone);
        const userId = phoneUserMap[normalizedPhone];

        if (userId) {
          filatomContacts.push({
            displayName: deviceContact.displayName,
            phones: deviceContact.phones,
            avatarUrl: deviceContact.avatarUrl || null,
            isOnFilatom: true,
            filatomUserId: userId,
          });
          found = true;
          break;
        }
      }
    }

    console.log(`Found ${filatomContacts.length} Filatom contacts`);
    return filatomContacts;
  }

  // ── Contacts NOT on Filatom ─────────────────────────────────────────────────

  async getNonFilatomContacts() {
    const deviceContacts = await this.getDeviceContacts();
    const filatomContacts = await this.getFilatomContactsOptimized();

    const filatomPhones = new Set();
    for (const c of filatomContacts) {
      for (const phone of c.phones) {
        filatomPhones.add(phone);
      }
    }

    const nonFilatom = deviceContacts.filter(c => 
      !c.phones.some(p => filatomPhones.has(p))
    );

    console.log(`Found ${nonFilatom.length} non-Filatom contacts`);
    return nonFilatom;
  }

  // ── Sync contacts to Firestore ─────────────────────────────────────────────

  async syncContacts() {
    const user = this._auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      const filatomContacts = await this.getFilatomContactsOptimized();

      // Save synced contacts to user document
      const userRef = doc(this._db, 'users', user.uid);
      await updateDoc(userRef, {
        syncedContacts: filatomContacts.map(c => ({
          userId: c.filatomUserId,
          name: c.displayName,
          phone: c.phones[0] || '',
          avatar: c.avatarUrl || '',
        })),
        contactsSyncedAt: new Date().toISOString(),
        syncContacts: true,
      });

      return { success: true, count: filatomContacts.length };
    } catch (error) {
      console.error('Error syncing contacts:', error);
      return { success: false, error: error.message };
    }
  }

  // ── Get synced contacts from Firestore ────────────────────────────────────

  async getSyncedContacts() {
    const user = this._auth.currentUser;
    if (!user) return [];

    try {
      const userRef = doc(this._db, 'users', user.uid);
      const docSnap = await getDoc(userRef);

      if (!docSnap.exists()) return [];

      const data = docSnap.data();
      return data.syncedContacts || [];
    } catch (error) {
      console.error('Error getting synced contacts:', error);
      return [];
    }
  }

  // ── Legacy aliases ──────────────────────────────────────────────────────────

  async getWrytteContactsOptimized() {
    return this.getFilatomContactsOptimized();
  }

  async getNonWrytteContacts() {
    return this.getNonFilatomContacts();
  }

  // ── Dispose ─────────────────────────────────────────────────────────────────

  dispose() {
    // Clean up any resources if needed
  }
}

// ── Singleton export ─────────────────────────────────────────────────────────

const contactService = ContactService.getInstance();
export { contactService };
export default contactService;